import React, { useEffect, useState } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { CategoryScale } from "chart.js";
import Chart from "chart.js/auto";
import { useGlobalContext } from "../../../../Contexts/GlobalContext";
import { 
  FaUsers, 
  FaClock, 
  FaDollarSign,
  FaChartLine,
  FaDownload,
  FaCircleCheck
} from "react-icons/fa6";
import { FaCheckCircle, FaPercentage, FaFilter, FaExclamationTriangle } from "react-icons/fa";
import { MdPhoneAndroid, MdComputer, MdTablet } from "react-icons/md";

Chart.register(CategoryScale);

const Reports = ({ data, id, singleSurveyStats }) => {
  const { user, organization } = useGlobalContext();
  const completesKeys = singleSurveyStats?.completes
    ? Object.keys(singleSurveyStats.completes)
    : [];
  const entrantsKeys = singleSurveyStats?.entrants
    ? Object.keys(singleSurveyStats.entrants)
    : [];
  const prescreenKeys = singleSurveyStats?.prescreens
    ? Object.keys(singleSurveyStats.prescreens)
    : [];
  const labels = [...new Set([...entrantsKeys, ...prescreenKeys, ...completesKeys])];
  const [tab, setTab] = useState(["ENTRANTS"]);
  const [graphData, setGraphData] = useState([]);

  useEffect(() => {
    // Initialize graph data based on active tabs
    const initialData = [];
    if (tab?.includes("ENTRANTS") && entrantsKeys?.length > 0) {
      initialData.push({
        label: "Entrants",
        data: entrantsKeys?.map((d) => singleSurveyStats?.entrants?.[d] || 0),
        backgroundColor: "#EE964B",
        barThickness: 40,
      });
    }
    if (tab?.includes("PRESCREENS") && prescreenKeys?.length > 0) {
      initialData.push({
        label: "Preescreens",
        data: prescreenKeys?.map((d) => singleSurveyStats?.prescreens?.[d] || 0),
        backgroundColor: "#F4D35E",
        barThickness: 40,
      });
    }
    if (tab?.includes("COMPLETES") && completesKeys?.length > 0) {
      initialData.push({
        label: "Completes",
        data: completesKeys?.map((d) => singleSurveyStats?.completes?.[d] || 0),
        backgroundColor: "#0226BE",
        barThickness: 40,
      });
    }
    setGraphData(initialData);
  }, [entrantsKeys?.length, prescreenKeys?.length, completesKeys?.length, tab]);

  // Calculate KPIs
  const totalEntrants = Object?.values(singleSurveyStats?.entrants || {})?.reduce(
    (acc, cur) => (acc += cur),
    0
  ) || 0;
  const totalPrescreens = Object?.values(singleSurveyStats?.prescreens || {})?.reduce(
    (acc, cur) => (acc += cur),
    0
  ) || 0;
  const totalCompletes = Object?.values(singleSurveyStats?.completes || {})?.reduce(
    (acc, cur) => (acc += cur),
    0
  ) || 0;
  const conversionRate = totalEntrants > 0 
    ? ((totalCompletes / totalEntrants) * 100).toFixed(2) 
    : 0;
  const completionRate = totalPrescreens > 0
    ? ((totalCompletes / totalPrescreens) * 100).toFixed(2)
    : 0;
  // Backend returns avgCpi (lowercase), not avgCPI
  // avgCpi is calculated from completed sessions only (client_status === 10)
  // If no completes yet, it will be 0, so we use survey setup CPI as fallback/reference
  const avgCPIFromStats = parseFloat(singleSurveyStats?.avgCpi || singleSurveyStats?.avgCPI || 0);
  const surveySetupCPI = parseFloat(data?.setup?.surveyCpi || 0);
  // Use actual avgCPI if available (from completed sessions), otherwise use survey setup CPI
  const avgCPI = avgCPIFromStats > 0 ? avgCPIFromStats : (totalCompletes === 0 ? surveySetupCPI : 0);
  
  // Get hits (total sessions) from stats
  const totalHits = singleSurveyStats?.hits || totalEntrants || 0;
  
  // EPC calculation: (Avg CPI * Total Completes) / Total Hits/Entrants
  // Formula from backend: (avgCPI * completes) / hits
  // If no actual data, calculate from setup CPI as reference
  const epc = singleSurveyStats?.epc || 
    (totalHits > 0 && avgCPI > 0 && totalCompletes > 0
      ? parseFloat(((avgCPI * totalCompletes) / totalHits).toFixed(2))
      : (totalHits > 0 && surveySetupCPI > 0 && totalCompletes > 0
        ? parseFloat(((surveySetupCPI * totalCompletes) / totalHits).toFixed(2))
        : 0));
  const avgLOI = Object?.values(singleSurveyStats?.avgLOI || {})?.reduce(
    (a, c) => (a += (c || 0)), 0
  ) / Math.max(Object?.values(singleSurveyStats?.avgLOI || {})?.length || 1, 1);
  const incidenceRate = singleSurveyStats?.incidenceRate || 0;
  const dropOffRate = singleSurveyStats?.dropOffRate || 0;

  const BarChart = () => {
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
              weight: '500'
            }
          }
        },
        title: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: {
            size: 14,
            weight: '600'
          },
          bodyFont: {
            size: 13
          },
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 11
            },
            color: '#666'
          }
        },
        y: {
          border: {
            display: false,
          },
          grid: {
            color: '#f0f0f0',
            drawBorder: false
          },
          ticks: {
            font: {
              size: 11
            },
            color: '#666'
          },
          beginAtZero: true
        },
      },
    };
    const chartData = {
      labels: labels?.slice(-7), // Last 7 days
      datasets: graphData,
    };
    return <Bar options={options} data={chartData} height={300} />;
  };

  const RateChart = ({ title, alertLevel, expected, avgDate, icon: Icon }) => {
    const percentage = avgDate || 0;
    const expectedVal = expected || 0;
    const isGood = percentage >= expectedVal * 0.9; // Within 90% of expected is good
    
    return (
      <div className="w-full flex flex-col gap-4 p-5 bg-white border border-[#E0E0E0] rounded-md hover:border-[#0226BE]/30 transition-colors">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="text-lg text-[#0226BE]" />}
            <h1 className="text-[#333] font-[500] text-[16px]">{title}</h1>
          </div>
          <span className={`text-[11px] border rounded px-2 py-1 font-[500] ${
            isGood ? 'text-[#666] border-[#E0E0E0]' : 'text-[#333] border-[#E0E0E0]'
          }`}>
            {isGood ? 'Normal' : alertLevel || 'High'}
          </span>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <p className="text-[13px] text-[#666] font-[400]">Expected:</p>
            <p className="text-[14px] font-[600] text-[#0226BE]">
              {expectedVal}%
            </p>
          </div>
          <div className="flex items-center gap-2 border-l border-[#E0E0E0] pl-6">
            <p className="text-[13px] text-[#666] font-[400]">Actual:</p>
            <p className="text-[14px] font-[600] text-[#333]">
              {percentage?.toFixed(2)}%
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden relative">
            <div
              style={{ width: `${Math.min(percentage, 100)}%` }}
              className="h-full bg-[#0226BE] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
            >
              <span className="text-white text-[11px] font-[500]">
                {percentage > 5 ? `${percentage?.toFixed(1)}%` : ''}
              </span>
            </div>
            {expectedVal > 0 && (
              <div
                style={{ left: `${Math.min(expectedVal, 100)}%` }}
                className="absolute top-0 bottom-0 w-0.5 bg-[#0226BE]"
              />
            )}
          </div>
          <div className="flex items-center justify-between text-[11px] text-[#999]">
            <span>0%</span>
            {expectedVal > 0 && (
              <span className="text-[#666] font-[400]">Expected: {expectedVal}%</span>
            )}
            <span>100%</span>
          </div>
        </div>
      </div>
    );
  };

  const LineChart = ({ title, alertLevel, expected, avgDate, data, icon: Icon, color }) => {
    // Calculate if LOI is within acceptable range (±20% of expected)
    const expectedNum = Number(expected) || 0;
    const avgNum = Number(avgDate) || 0;
    const difference = avgNum - expectedNum;
    const percentageDiff = expectedNum > 0 ? (Math.abs(difference) / expectedNum) * 100 : 0;
    const isGood = percentageDiff <= 20; // Within 20% is acceptable
    const variance = difference > 0 ? 'above' : 'below';
    const varianceAmount = Math.abs(difference).toFixed(2);
    
    // Determine alert level based on variance
    let calculatedAlertLevel = 'Normal';
    let alertColor = 'green';
    if (percentageDiff > 50) {
      calculatedAlertLevel = 'Critical';
      alertColor = 'red';
    } else if (percentageDiff > 30) {
      calculatedAlertLevel = 'High';
      alertColor = 'orange';
    } else if (percentageDiff > 20) {
      calculatedAlertLevel = 'Medium';
      alertColor = 'yellow';
    }
    
    const LineChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
              weight: '500'
            }
          }
        },
        title: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: {
            size: 14,
            weight: '600'
          },
          bodyFont: {
            size: 13
          }
        }
      },
      scales: {
        x: {
          grid: {
            drawOnChartArea: true,
            color: "#F0F0F0",
            drawBorder: false
          },
          ticks: {
            font: {
              size: 11
            },
            color: "#666"
          },
          border: {
            display: false,
          },
        },
        y: {
          grid: {
            drawOnChartArea: false,
            display: false,
          },
          ticks: {
            font: {
              size: 11
            },
            callback: function (value) {
              return Number(value)?.toFixed(1) + " Min";
            },
            color: "#666",
          },
          border: {
            display: false,
          },
        },
      },
    };

    const alertStyles = {
      'Normal': 'text-green-700 border-green-300 bg-green-50',
      'Medium': 'text-yellow-700 border-yellow-300 bg-yellow-50',
      'High': 'text-orange-700 border-orange-300 bg-orange-50',
      'Critical': 'text-red-700 border-red-300 bg-red-50'
    };

    return (
      <div className="w-full flex flex-col gap-5 p-5 bg-white border border-[#E0E0E0] rounded-md hover:border-[#0226BE]/30 transition-colors">
        {/* Header */}
        <div className="w-full flex items-center justify-between pb-3 border-b border-[#E0E0E0]">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="text-lg text-[#0226BE]" />}
            <h1 className="text-[#333] font-[500] text-[16px]">{title}</h1>
          </div>
          <span className={`text-[11px] border rounded px-2 py-1 font-[500] ${
            isGood ? 'text-[#666] border-[#E0E0E0] bg-white' : 'text-[#333] border-[#E0E0E0] bg-white'
          }`}>
            {calculatedAlertLevel}
          </span>
        </div>

        {/* Metrics Comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 border border-[#0226BE]/20 bg-blue-50/30 rounded-md">
            <p className="text-[12px] text-[#666] font-[400] mb-1">Expected</p>
            <p className="text-[20px] font-[600] text-[#0226BE]">
              {expectedNum.toFixed(1)} <span className="text-[14px] font-[400]">Min</span>
            </p>
          </div>
          <div className={`p-3 border rounded-md ${
            isGood ? 'border-[#E0E0E0]' : 'border-[#E0E0E0]'
          }`}>
            <p className="text-[12px] text-[#666] font-[400] mb-1">Average</p>
            <p className="text-[20px] font-[600] text-[#333]">
              {avgNum.toFixed(2)} <span className="text-[14px] font-[400]">Min</span>
            </p>
            {!isGood && (
              <p className="text-[11px] mt-1 text-[#666] font-[400]">
                {varianceAmount} min {variance} ({percentageDiff.toFixed(1)}%)
              </p>
            )}
          </div>
        </div>

        {/* Chart */}
        <div className="h-[200px]">
          <Line options={LineChartOptions} data={data} />
        </div>
      </div>
    );
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, color, bgColor, trend, trendValue }) => {
    return (
      <div className="p-5 border border-[#E0E0E0] bg-white rounded-md hover:border-[#0226BE]/30 transition-colors">
        <div className="flex items-start justify-between mb-3">
          {Icon && <Icon className="text-xl text-[#0226BE]" />}
        </div>
        <h3 className="text-[#666] text-[13px] font-[400] mb-1">{title}</h3>
        <p className="text-[#333] text-[28px] font-[600] mb-1">{value}</p>
        {subtitle && (
          <p className="text-[#999] text-[12px] font-[400]">{subtitle}</p>
        )}
      </div>
    );
  };

  const terminationLOIData = {
    labels: Object?.keys(singleSurveyStats?.terminationLOI || {})
      ?.map((d) =>
        new Date(d)?.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
        })
      )
      ?.slice(-7),
    datasets: [
      {
        axis: "y",
        label: "Average Length Of Interview",
        data: Object?.values(singleSurveyStats?.terminationLOI || {})
          ?.map((d) => (d === null ? 0 : d))
          ?.slice(-7),
        fill: true,
        backgroundColor: 'rgba(2, 38, 190, 0.1)',
        borderColor: "#0226BE",
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 5,
        pointBackgroundColor: "#0226BE",
        pointHoverRadius: 7,
        pointHoverBorderWidth: 2,
      },
      {
        axis: "y",
        label: "Expected Length Of Interview",
        data: Array(7).fill(data?.expectedMatrix?.expectedCompletionLOI || 0),
        borderColor: "#F68D8D",
        borderDash: [5, 5],
        fill: false,
        borderWidth: 2,
        pointRadius: 0,
      },
    ],
  };

  const lengthOfInterviewData = {
    labels: Object?.keys(singleSurveyStats?.avgLOI || {})
      ?.map((d) =>
        new Date(d)?.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
        })
      )
      ?.slice(-7),
    datasets: [
      {
        axis: "y",
        label: "Length of Interview",
        data: Object?.values(singleSurveyStats?.avgLOI || {})
          ?.map((d) => (d === null ? 0 : d))
          ?.slice(-7),
        fill: true,
        backgroundColor: 'rgba(2, 38, 190, 0.1)',
        borderColor: "#0226BE",
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 5,
        pointBackgroundColor: "#0226BE",
        pointHoverRadius: 7,
      },
      {
        axis: "y",
        label: "Expected Length Of Interview",
        data: Array(7).fill(data?.expectedMatrix?.expectedCompletionLOI || 0),
        borderColor: "#F68D8D",
        borderDash: [5, 5],
        fill: false,
        borderWidth: 2,
        pointRadius: 0,
      },
    ],
  };

  return (
    <div className="flex flex-col w-full gap-[32px] pb-6">
      {/* Header */}
      <div className="w-full py-4 px-6 bg-white border-b border-[#E0E0E0]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[#0226BE] text-[24px] font-[500] mb-1">
              Survey Analytics & Reports
            </h1>
            <p className="text-[#666] text-[14px] font-[400]">
              Performance metrics for {data?.surveyName}
            </p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#0226BE] text-white rounded-md hover:bg-[#021ea8] transition-all text-[14px] font-[500]">
            <FaDownload />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Entrants"
          value={totalEntrants.toLocaleString()}
          subtitle="Respondents who entered survey"
          icon={FaUsers}
          color="bg-orange-100"
          trend={totalEntrants > 0 ? 'up' : null}
          trendValue={totalEntrants > 0 ? 'Active' : '0'}
        />
        <KPICard
          title="Total Completes"
          value={totalCompletes.toLocaleString()}
          subtitle={`${conversionRate}% conversion rate`}
          icon={FaCheckCircle}
          color="bg-green-100"
          trend={totalCompletes > 0 ? 'up' : null}
          trendValue={totalCompletes > 0 ? 'Active' : '0'}
        />
        <KPICard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          subtitle={`${totalCompletes}/${totalEntrants} completes`}
          icon={FaPercentage}
          color="bg-blue-100"
        />
        <KPICard
          title="Avg. CPI"
          value={`$${avgCPI.toFixed(2)}`}
          subtitle={
            avgCPIFromStats > 0 
              ? "From completed sessions" 
              : totalCompletes === 0 
                ? surveySetupCPI > 0 
                  ? `Expected: $${surveySetupCPI.toFixed(2)}` 
                  : "No CPI configured"
                : "No completed sessions yet"
          }
          icon={FaDollarSign}
          color="bg-purple-100"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Avg. LOI"
          value={`${avgLOI.toFixed(1)} Min`}
          subtitle="Average length of interview"
          icon={FaClock}
          color="bg-cyan-100"
        />
        <KPICard
          title="EPC"
          value={`$${epc.toFixed(2)}`}
          subtitle={
            epc > 0 
              ? `From ${totalCompletes} completes` 
              : totalCompletes === 0 
                ? "No completes to calculate" 
                : "Calculating..."
          }
          icon={FaChartLine}
          color="bg-yellow-100"
        />
        <KPICard
          title="Incidence Rate"
          value={`${incidenceRate.toFixed(1)}%`}
          subtitle="Qualification rate"
          icon={FaPercentage}
          color="bg-pink-100"
        />
        <KPICard
          title="Drop Off Rate"
          value={`${dropOffRate.toFixed(1)}%`}
          subtitle="Survey abandonment"
          icon={FaChartLine}
          color="bg-red-100"
        />
      </div>

      {/* Respondent Activity Chart */}
      <div className="w-full bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E0E0E0]">
          <div>
            <h1 className="text-[#0226BE] text-[18px] font-[500] mb-1">
              Respondent Activity
            </h1>
            <p className="text-[#666] text-[13px] font-[400]">Daily activity breakdown over the last 7 days</p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="px-5 py-4 border-b border-[#E0E0E0]">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className={`flex items-center gap-2 px-4 py-2 text-[13px] font-[500] rounded-md transition-all ${
                tab?.includes("ENTRANTS")
                  ? "bg-[#0226BE] text-white"
                  : "bg-white text-[#666] border border-[#E0E0E0] hover:border-[#0226BE]"
              }`}
              onClick={(e) => {
                if (tab?.includes("ENTRANTS")) {
                  setTab((prev) => prev?.filter((data) => data !== "ENTRANTS"));
                } else {
                  setTab((prev) => [...prev, "ENTRANTS"]);
                }
              }}
            >
              <FaUsers className="text-sm" />
              <span>ENTRANTS</span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-[500] ${
                tab?.includes("ENTRANTS")
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-[#666]"
              }`}>
                {totalEntrants.toLocaleString()}
              </span>
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 text-[13px] font-[500] rounded-md transition-all ${
                tab?.includes("PRESCREENS")
                  ? "bg-[#0226BE] text-white"
                  : "bg-white text-[#666] border border-[#E0E0E0] hover:border-[#0226BE]"
              }`}
              onClick={(e) => {
                if (tab?.includes("PRESCREENS")) {
                  setTab((prev) => prev?.filter((data) => data !== "PRESCREENS"));
                } else {
                  setTab((prev) => [...prev, "PRESCREENS"]);
                }
              }}
            >
              <FaFilter className="text-sm" />
              <span>PRESCREENS</span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-[500] ${
                tab?.includes("PRESCREENS")
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-[#666]"
              }`}>
                {totalPrescreens.toLocaleString()}
              </span>
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 text-[13px] font-[500] rounded-md transition-all ${
                tab?.includes("COMPLETES")
                  ? "bg-[#0226BE] text-white"
                  : "bg-white text-[#666] border border-[#E0E0E0] hover:border-[#0226BE]"
              }`}
              onClick={(e) => {
                if (tab?.includes("COMPLETES")) {
                  setTab((prev) => prev?.filter((data) => data !== "COMPLETES"));
                } else {
                  setTab((prev) => [...prev, "COMPLETES"]);
                }
              }}
            >
              <FaCheckCircle className="text-sm" />
              <span>COMPLETES</span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-[500] ${
                tab?.includes("COMPLETES")
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-[#666]"
              }`}>
                {totalCompletes.toLocaleString()}
              </span>
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="p-5">
          <div className="h-[350px]">
            <BarChart />
          </div>
        </div>
      </div>

      {/* Line Charts */}
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LineChart
          title="Termination LOI"
          expected={data?.expectedMatrix?.expectedCompletionLOI || 0}
          avgDate={Number(
            Object?.values(singleSurveyStats?.terminationLOI || {})
              ?.slice(-7)
              ?.reduce((a, c) => (a += (c || 0)), 0) /
              Math.max(Object?.values(singleSurveyStats?.terminationLOI || {})?.slice(-7)?.length || 1, 1)
          )}
          data={terminationLOIData}
          icon={FaClock}
          color="text-blue-600"
        />
        <LineChart
          title="Length Of Interview"
          expected={data?.expectedMatrix?.expectedCompletionLOI || 0}
          avgDate={avgLOI}
          data={lengthOfInterviewData}
          icon={FaClock}
          color="text-indigo-600"
        />
      </div>

      {/* Rate Charts */}
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RateChart
          title="Incidence Rate"
          alertLevel="High"
          expected={data?.expectedMatrix?.expectedIncidenceRate || 0}
          avgDate={incidenceRate}
          icon={FaPercentage}
        />
        <RateChart
          title="Drop Off Rate"
          alertLevel="High"
          expected={organization?.projectSettings?.expectedDropOfRate || 0}
          avgDate={dropOffRate}
          icon={FaChartLine}
        />
      </div>

      {organization?.projectSettings?.expectedQuotaRate && (
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RateChart
            title="Quota Rate"
            alertLevel="High"
            expected={organization?.projectSettings?.expectedQuotaRate || 0}
            avgDate={singleSurveyStats?.quotaRate || 0}
            icon={FaPercentage}
          />
          <div></div>
        </div>
      )}
    </div>
  );
};

export default Reports;
